import Vue from 'vue'

import ApiService from '@/core/services/api.service'
import JwtService from '@/core/services/jwt.service'

import { LOGOUT } from './auth.module.js'
import router from '@/router'

import { eventBus } from '@/main'

// action types
export const GET_VPN_SERVERS = 'getVpnServers'
export const GET_VPN_SERVER = 'getVpnServer'
export const SWITCH_VPN_SERVER = 'switchVpnServer'
export const MANAGE_VPN_SERVER = 'manageVpnServer'
export const CHECK_VPN_SERVER_MANAGE_STATUS = 'checkVpnServerManageStatus'
export const DELETE_VPN_SERVER = 'deleteVpnServer'

// mutation types
export const SAVE_VPN_SERVERS = 'saveVpnServers'
export const SAVE_VPN_SERVER = 'saveVpnServer'
export const SAVE_REINSTALL_VPN_SERVER_PARAMS = 'saveReinstallVpnServerParams'
export const UPDATE_VPN_SERVERS = 'updateVpnServers'
export const SET_VPN_SERVER_PENDING_STATE = 'setVpnServerPendingState'
export const SET_VPN_SERVERS_LIMIT = 'setVpnServersLimit'
export const SET_VPN_SERVERS_PAGE = 'setVpnServersPage'
export const CLEAR_VPN_SERVERS = 'clearVpnServers'
export const SELECT_VPN_SERVER = 'selectVpnServer'
export const UNSELECT_VPN_SERVER = 'unselectVpnServer'
export const MARK_FOR_SWITCH_VPN_SERVER = 'markForSwitchVpnServer'
export const UNMARK_FOR_SWITCH_VPN_SERVER = 'unmarkForSwitchVpnServer'
export const ADD_NEW_VPN_SERVER_PARAM = 'addNewVpnServerParam'
export const DELETE_VPN_SERVER_PARAM = 'deleteVpnServerParam'
export const SAVE_MANAGE_VPN_SERVER_ERRORS = 'saveManageVpnServerErrors'
export const CLEAR_MANAGE_VPN_SERVER_ERRORS = 'clearManageVpnServerErrors'

const SET_RESPONSE_STATE = 'setResponseState'
const TOAST_VPN_SERVERS = 'showToast/SettingsVpnServers'

// returns vpn server based on code
function findVpnServer(state, code) {
  return state.vpnServersList.filter(item => item.code === code)[0]
}

// Back-end GET/POST/PUT requests required and returned data in format that I was not comfortable to wotk with on front-end, so I've managed to transform it both at both receiving and sending
function convertForBackend(context) {
  const income = { ...context.state.selectedVpnServer }

  // change format of { parameters } object
  income.data = {}
  income.data_public_list = []
  const paramsList = context.state.selectedVpnServer.parameters
  for (let i = 0; i < paramsList.length; i++) {
    income.data[paramsList[i].name] = paramsList[i].code
    if (paramsList[i].isPublic) {
      income.data_public_list.push(paramsList[i].name)
    }
  }
  delete income.parameters

  // change format of .active param
  income.active = income.active ? 'on' : 'off'

  // if PUT method is expected, delete everything except { data, data_public_list }
  if (context.getters.selectedVpnServerExist) {
    Object.keys(income).forEach(key => {
      if (['data', 'data_public_list'].includes(key)) {
        return
      }
      delete income[key]
    })
  }

  return income
}

function convertForFrontEnd(server) {
  const income = { ...server }

  // change format of { data } object
  income.parameters = []
  if (income.data) {
    Object.keys(income.data).forEach(key => {
      income.parameters.push({
        name: key,
        code: income.data[key],
        isPublic: income.data_public_list ? income.data_public_list.includes(key) : false
      })
    })
  }

  // change format of .active param
  income.active = income.active == 'on'

  return income
}

function convertReinstallParams(params) {
  let result = []
  Object.keys(params.data).forEach(key => {
    result.push({
      name: key,
      code: params.data[key]
    })
  })
  return result
}

const state = {
  vpnServersList: [],
  selectedVpnServer: {
    parameters: []
  },
  selectedVpnServerId: null,
  markedForSwitchVpnServer: {},
  manageVpnServerErrors: [],

  responses: {
    vpnServersList: false,
    checkVpnServerManageStatus: false,
    vpnServer: false,
    vpnServerStatus: false,
    switchVpnServer: false,
    manageVpnServer: false,
    deleteVpnServer: false,
  },
}

const getters = {
  vpnServersList(state) {
    return state.vpnServersList.sort((a, b) => { return (a._id < b._id) ? -1 : (a._id > b._id) ? 1 : 0 })
  },
  vpnServersEmpty(state) {
    return state.vpnServersList.length == 0
  },
  totalVpnServers(state) {
    return state.vpnServersList.length
  },
  selectedVpnServer(state) {
    return state.selectedVpnServer
  },
  selectedVpnServerExist(state) {
    return state.vpnServersList.some(server => server._id == state.selectedVpnServerId)
  },
  markedForSwitchVpnServer(state) {
    return state.markedForSwitchVpnServer
  },
  someVpnServerPending(state) {
    return state.vpnServersList.some(server => server.pending)
  },
  noneVpnServerPending(state) {
    return state.vpnServersList.filter(server => server.pending).length == 0
  },
  installedVpnServersCount(state) {
    return state.vpnServersList.filter(server => server.installed).length
  },
  vpnServersModalTitle(state) {
    return state.selectedVpnServerId ? 'Edit VPN Server' : 'Add New VPN Server'
  },
  vpnServerParamsLength: state => code => {
    const target = findVpnServer(state, code)

    if (target && target.data) {
      return Object.keys(target.data).length
    } else {
      return 0
    }
  },
  vpnServerInstallBadge: () => server => {
    const badgeOptions = {
      variant: null,
      icon: null,
      iconColor: null,
      text: null,
    }

    if (server.pending) {
      badgeOptions.variant = 'info'
      badgeOptions.icon = 'spinner'
      badgeOptions.iconColor = 'white'
      badgeOptions.text = 'Pending'
    } else {
      badgeOptions.variant = server.installed ? 'success' : 'light'
      badgeOptions.icon = server.installed ? 'check' : 'minus'
      badgeOptions.iconColor = server.installed ? 'white' : 'dark'
      badgeOptions.text = server.installed ? 'Installed' : 'Not Installed'
    }

    return badgeOptions
  },
  manageVpnServerErrors(state) {
    return state.manageVpnServerErrors
  },
  manageVpnServerError: state => field => {
    return state.manageVpnServerErrors.filter(err => err.field == field)
  },
  vpnServersResponses: state => type => {
    return state.responses[type]
  }
}

const actions = {
  [GET_VPN_SERVERS](context, { server_id = undefined }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'vpnServersList', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        const url = server_id
          ? `${process.env.VUE_APP_SERVER}/${server_id}/vpn`
          : process.env.VUE_APP_VPN_SERVERS
        ApiService.get(url)
          .then(({ data }) => {
            context.commit(SAVE_VPN_SERVERS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_VPN_SERVERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'vpnServersList', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [GET_VPN_SERVER](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'vpnServer', status: true })
        eventBus.$emit('startProgress')
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_VPN_SERVER}/${id}`)
          .then(({ data }) => {
            context.commit(SAVE_VPN_SERVER, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_VPN_SERVERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'vpnServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [SWITCH_VPN_SERVER](context, { id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'switchVpnServer', status: true })
        eventBus.$emit('startProgress')
        const switchParam = state.markedForSwitchVpnServer.active ? 'disable' : 'enable'
        ApiService.put(`${process.env.VUE_APP_VPN_SERVER}/${state.markedForSwitchVpnServer._id}/${switchParam}`)
          .then(({ data }) => {
            context.dispatch(GET_VPN_SERVER, { id })
            eventBus.$emit(TOAST_VPN_SERVERS, {
              message: data.active == 'on' ? `VPN Server «${data.name}» enabled` : `VPN Server «${data.name}» disabled`,
              variant: data.active == 'on' ? 'success' : 'warning'
            })
            resolve(data)
          })
          .catch(err  => {
            eventBus.$emit(TOAST_VPN_SERVERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'switchVpnServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [MANAGE_VPN_SERVER](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'manageVpnServer', status: true })
        eventBus.$emit('startProgress')

        const payload = convertForBackend(context)
        let method
        let url

        if (context.getters.selectedVpnServerExist) {
          method = 'put'
          url = `${process.env.VUE_APP_VPN_SERVER}/${state.selectedVpnServerId}/data`
        } else {
          method = 'post'
          url = process.env.VUE_APP_VPN_SERVER
        }

        ApiService[method](url, payload)
          .then(({ data }) => {
            context.dispatch(GET_VPN_SERVERS, {})
            eventBus.$emit(TOAST_VPN_SERVERS, {
              message: context.getters.selectedVpnServerExist ? 'VPN Server updated' : 'Added new VPN Server',
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err  => {
            const error = err.response.data
            context.commit(SAVE_MANAGE_VPN_SERVER_ERRORS, error.errors)
            eventBus.$emit(TOAST_VPN_SERVERS, {
              message: error.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'manageVpnServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [CHECK_VPN_SERVER_MANAGE_STATUS](context, { server_id }) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'checkVpnServerManageStatus', status: true })
        ApiService.setHeader()
        ApiService.get(`${process.env.VUE_APP_SERVER}/${server_id}/vpn`)
          .then(({ data }) => {
            context.commit(UPDATE_VPN_SERVERS, data)
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_VPN_SERVERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'checkVpnServerManageStatus', status: false })
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
  [DELETE_VPN_SERVER](context) {
    return new Promise((resolve, reject) => {
      if (JwtService.getToken()) {
        context.commit(SET_RESPONSE_STATE, { type: 'deleteVpnServer', status: true })
        eventBus.$emit('startProgress')

        ApiService.delete(`${process.env.VUE_APP_VPN_SERVER}/${state.selectedVpnServerId}`)
          .then(({ data }) => {
            context.dispatch(GET_VPN_SERVERS, {})
            eventBus.$emit(TOAST_VPN_SERVERS, {
              message: data.message,
              variant: 'success'
            })
            resolve(data)
          })
          .catch(err => {
            eventBus.$emit(TOAST_VPN_SERVERS, {
              message: err.message,
              variant: 'danger'
            })
            reject(err)
          })
          .finally(() => {
            context.commit(SET_RESPONSE_STATE, { type: 'deleteVpnServer', status: false })
            eventBus.$emit('endProgress')
          })
      } else {
        context.dispatch(LOGOUT, null, { root: true })
          .then(() => router.push({ name: 'login' }))
          .catch(() => {})
      }
    })
  },
}

const mutations = {
  [SAVE_VPN_SERVERS](state, vpnServers) {
    state.vpnServersList = vpnServers
  },
  [SAVE_VPN_SERVER](state, vpnServer) {
    const target = findVpnServer(state, vpnServer.code)
    if (target) {
      const convertedVpnServer = convertForFrontEnd(vpnServer)
      Object.keys(vpnServer).forEach(key => {
        Vue.set(target, key, convertedVpnServer[key])
      })
    }
  },
  [SAVE_REINSTALL_VPN_SERVER_PARAMS](state, { code, params }) {
    const target = findVpnServer(state, code)
    if (target) {
      const convertedReinstallParams = convertReinstallParams(params)
      Vue.set(target, 'reinstallParams', convertedReinstallParams)
    }
  },
  [UPDATE_VPN_SERVERS](state, vpnServers) {
    if (!vpnServers || vpnServers.length === 0) {
      return
    }

    vpnServers.forEach(({ code, installed, data }) => {
      const target = findVpnServer(state, code)
  
      if (target) {
        if (installed !== target.installed) {
          Vue.set(target, 'installed', installed)
          Vue.set(target, 'pending', false)
          target.data = data || []
        }
      }
    })
  },
  [SET_VPN_SERVER_PENDING_STATE](state, vpn_code) {
    const target = findVpnServer(state, vpn_code)
  
    if (target) {
      Vue.set(target, 'pending', true)
    }
  },
  [CLEAR_VPN_SERVERS](state) {
    state.vpnServersList = []
  },
  [SET_VPN_SERVERS_LIMIT](state, limit) {
    state.vpnServersGetParams.limit = limit
  },
  [SET_VPN_SERVERS_PAGE](state, page) {
    state.vpnServersGetParams.page = page
  },
  [SELECT_VPN_SERVER](state, vpnServer) {
    state.selectedVpnServer = convertForFrontEnd(vpnServer)
    state.selectedVpnServerId = vpnServer._id
  },
  [UNSELECT_VPN_SERVER](state) {
    state.selectedVpnServer = {}
    Vue.set(state.selectedVpnServer, 'parameters', [])
    state.selectedVpnServerId = null
  },
  [MARK_FOR_SWITCH_VPN_SERVER](state, vpnServer) {
    state.markedForSwitchVpnServer = vpnServer
  },
  [UNMARK_FOR_SWITCH_VPN_SERVER](state) {
    state.markedForSwitchVpnServer = {}
  },
  [ADD_NEW_VPN_SERVER_PARAM](state, index) {
    Vue.set(state.selectedVpnServer.parameters, index, { name: '', code: '' })
  },
  [DELETE_VPN_SERVER_PARAM](state, index) {
    Vue.delete(state.selectedVpnServer.parameters, index)
  },
  [SAVE_MANAGE_VPN_SERVER_ERRORS](state, errors) {
    state.manageVpnServerErrors = errors
  },
  [CLEAR_MANAGE_VPN_SERVER_ERRORS](state) {
    state.manageVpnServerErrors = []
  },
  [SET_RESPONSE_STATE](state, { type, status }) {
    state.responses[type] = status
  },
}

export default {
  state,
  actions,
  mutations,
  getters,
}
